import axios from 'axios';
import { useRef, useState } from 'react';

import apiToken, {
  handleApiError,
  isSuccess,
} from '@/shared/services/axiosInstance';

import { S3FileType, S3UploadOptions } from '../types/S3Types';

const ALLOWED_UPLOAD_TYPES = [
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-ms-wmv',
  'video/x-flv',
  'video/webm',
];

const ALLOWED_FILE_TYPES_MESSAGE =
  'allowed file type: jpg, jpeg, png, gif, webp, svg, pdf, mp4, mov, avi, wmv, flv, webm';

export const isAllowedUploadType = (type: string) => {
  return ALLOWED_UPLOAD_TYPES.includes(type);
};

export const useS3 = ({
  fileType = 'BUSINESS_CERT',
  onUploadSuccess,
  onUploadError,
}: S3UploadOptions) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedUploadType(file.type)) {
      alert(ALLOWED_FILE_TYPES_MESSAGE);
      onUploadError?.(ALLOWED_FILE_TYPES_MESSAGE);
      setSelectedFile(null);
      event.target.value = '';
      return;
    }

    setSelectedFile(file);

    const s3FileUrl = await uploadToS3(file);

    if (s3FileUrl) {
      setUploadedUrl(s3FileUrl);
      onUploadSuccess?.(s3FileUrl);
    }
  };

  const uploadToS3 = async (file: File) => {
    setUploadLoading(true);
    try {
      const formData = {
        fileInfoList: [
          {
            fileType,
            fileName: file.name,
          },
        ],
      };

      const response = await apiToken.post('/api/v1/s3/upload', formData);

      if (isSuccess(response.data)) {
        const urlList = response.data.data?.urlList;
        const uploadUrl = urlList && urlList.length > 0 ? urlList[0].url : '';

        if (uploadUrl) {
          await axios.put(uploadUrl, file, {
            headers: {
              'Content-Type': file.type,
            },
          });
          return uploadUrl;
        }
      }
      return null;
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('File upload failed:', errorMessage);
      alert(errorMessage);
      onUploadError?.(errorMessage);
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      return null;
    } finally {
      setUploadLoading(false);
    }
  };

  return {
    handleFileSelect,
    handleUploadClick,
    selectedFile,
    uploadLoading,
    uploadedUrl,
    fileInputRef,
  };
};

export const uploadFile = async (
  file: File,
  fileType: S3FileType = 'BUSINESS_CERT',
): Promise<string | null> => {
  try {
    if (!isAllowedUploadType(file.type)) {
      alert(ALLOWED_FILE_TYPES_MESSAGE);
      return null;
    }

    const formData = {
      fileInfoList: [
        {
          fileType,
          fileName: file.name,
        },
      ],
    };

    const response = await apiToken.post('/api/v1/s3/upload', formData);

    if (isSuccess(response.data)) {
      const urlList = response.data.data?.urlList;
      const uploadUrl = urlList && urlList.length > 0 ? urlList[0].url : null;
      if (uploadUrl) {
        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
        });
      }
      return uploadUrl;
    }
    return null;
  } catch (error) {
    const errorMessage = handleApiError(error);
    console.error('File upload failed:', errorMessage);
    alert(errorMessage);
    return null;
  }
};

export const deleteFile = async (fileUrl: string): Promise<boolean> => {
  try {
    let filePath = '';

    try {
      const url = new URL(fileUrl);
      filePath = url.pathname.startsWith('/')
        ? url.pathname.slice(1)
        : url.pathname;
    } catch {
      filePath = fileUrl;
    }

    const response = await apiToken.delete('/api/v1/s3', {
      data: { filePathList: [filePath] },
    });

    return isSuccess(response.data);
  } catch (error) {
    const errorMessage = handleApiError(error);
    console.error('File delete failed:', errorMessage);
    alert(errorMessage);
    return false;
  }
};
